import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isNative, scanBarcode, hapticSuccess } from '../native';

export default function BarcodeScanner({ onScan, onClose }) {
  const { t } = useTranslation();
  const videoRef    = useRef(null);
  const readerRef   = useRef(null);

  // Refs til callbacks — opdateres løbende, men effecten re-kører IKKE
  const onScanRef  = useRef(onScan);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onScanRef.current  = onScan;  }, [onScan]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const [status, setStatus] = useState('starting');
  const [error, setError]   = useState('');

  const stop = useCallback(() => {
    if (readerRef.current) {
      try { readerRef.current.reset(); } catch {}
      readerRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
  }, []);

  // ── Native ML Kit — kører KUN én gang (tom dep-liste) ──────────────────
  useEffect(() => {
    if (!isNative()) return;

    let settled = false; // Guard: onScan/onClose kaldes max én gang

    const finish = (code) => {
      if (settled) return;
      settled = true;
      if (code) { hapticSuccess(); onScanRef.current(code); }
      else onCloseRef.current();
    };

    scanBarcode()
      .then(code => finish(code))
      .catch(() => finish(null));

    return () => { settled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Web fallback (@zxing) ───────────────────────────────────────────────
  useEffect(() => {
    if (isNative()) return;

    let cancelled = false;
    const start = async () => {
      try {
        const { BrowserMultiFormatReader, NotFoundException } = await import('@zxing/browser');
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        const device = devices.find(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('environment') ||
          d.label.toLowerCase().includes('rear')
        ) || devices[devices.length - 1];
        if (!device) throw new Error('Intet kamera fundet');
        setStatus('live');
        await reader.decodeFromVideoDevice(device.deviceId, videoRef.current, (result, err) => {
          if (result) { stop(); hapticSuccess(); onScanRef.current(result.getText()); }
          if (err && !(err instanceof NotFoundException)) console.warn('Scanner:', err);
        });
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e.message || 'Kamera fejl'); }
      }
    };
    start();
    return () => { cancelled = true; stop(); };
  }, [stop]); // stop er stabil (useCallback med tom deps)

  // Native: scan() har sin egen fuldskærms-kamera-UI — intet React-overlay
  if (isNative()) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#000' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'white', fontSize: 16, fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>Scan stregkode</span>
        <button type="button" onClick={() => { stop(); onCloseRef.current(); }}
          style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
      </div>
      <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ width: 240, height: 160, position: 'relative' }}>
          {[['0,0','tl'],['auto,0','tr'],['0,auto','bl'],['auto,auto','br']].map(([pos, key]) => {
            const [t,r,b,l] = pos==='0,0'?['0','auto','auto','0']:pos==='auto,0'?['0','0','auto','auto']:pos==='0,auto'?['auto','auto','0','0']:['auto','0','0','auto'];
            return <div key={key} style={{ position:'absolute', width:24, height:24, top:t, right:r, bottom:b, left:l, borderColor:'#0d9e72', borderStyle:'solid', borderWidth:0, borderTopWidth:t==='0'?3:0, borderRightWidth:r==='0'?3:0, borderBottomWidth:b==='0'?3:0, borderLeftWidth:l==='0'?3:0 }} />;
          })}
        </div>
      </div>
      {status === 'starting' && <div style={{ position:'absolute', bottom:120, left:0, right:0, textAlign:'center' }}><p style={{ color:'rgba(255,255,255,0.7)', fontSize:14, fontFamily:'DM Sans, sans-serif' }}>{t('scanner.starting')}</p></div>}
      {status === 'error' && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.8)', padding:24, textAlign:'center' }}>
          <p style={{ color:'#ff6b6b', fontSize:14, fontFamily:'DM Sans, sans-serif', marginBottom:12 }}>{error}</p>
          <button type="button" onClick={() => { stop(); onCloseRef.current(); }} style={{ background:'#0d9e72', color:'white', border:'none', borderRadius:10, padding:'10px 24px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>{t('common.close')}</button>
        </div>
      )}
    </div>
  );
}
